import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import PartnerListingDataContributionWizard from "../../components/partner/PartnerListingDataContributionWizard";
import {
  fetchCasaMapaPartnerListingHealth,
  updateCasaMapaPartnerListingSync,
  updateCasaMapaPartnerListingVisibility,
} from "../../services/casamapaPartnerAgencyApi";
import { useLocale, useCurrency } from "../../intl";
import "../../styles/partnerPortalDashboard.css";
import "../../styles/casamapaListingHealthDashboard.css";
import "../../styles/partnerListingsDashboard.css";

const COPY = "realEstate.casaMapa.admin.partnerListings";
const HEALTH_COPY = "realEstate.casaMapa.admin.listings";
const QUEUE_PAGE_SIZE = 20;

function formatListingsLoadError(err, t) {
  const message = String(err?.message || "");
  if (/route not found/i.test(message)) {
    return t("realEstate.casaMapa.admin.partnerListings.apiUnavailable");
  }
  return message || t("realEstate.casaMapa.admin.errors.loadListings");
}

function parseIssueFilters(searchParams) {
  const raw = searchParams.get("issues") || searchParams.get("issue") || "";
  return raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

function IssueTypeFilterMenu({ issueOptions, selectedIssues, onToggle, onClear, t }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const buttonLabel =
    selectedIssues.length === 0
      ? t(`${HEALTH_COPY}.issueTypeFilterAll`)
      : selectedIssues.length === 1
        ? t(`${HEALTH_COPY}.issues.${selectedIssues[0]}`)
        : t(`${HEALTH_COPY}.issueTypeFilterSelected`, { count: selectedIssues.length });

  return (
    <div className="cm-listing-health__issue-filter" ref={rootRef}>
      <button
        type="button"
        className="cm-listing-health__issue-filter-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{t(`${HEALTH_COPY}.issueTypeFilterLabel`)}</span>
        <strong>{buttonLabel}</strong>
        <span className="cm-listing-health__issue-filter-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open ? (
        <div className="cm-listing-health__issue-filter-menu" role="listbox" aria-multiselectable="true">
          <div className="cm-listing-health__issue-filter-menu-head">
            <span>{t(`${HEALTH_COPY}.issueTypeFilterLabel`)}</span>
            {selectedIssues.length ? (
              <button type="button" className="cm-listing-health__issue-filter-clear" onClick={onClear}>
                {t(`${HEALTH_COPY}.clearFilter`)}
              </button>
            ) : null}
          </div>
          {issueOptions.map((issue) => {
            const checked = selectedIssues.includes(issue.id);
            return (
              <label key={issue.id} className="cm-listing-health__issue-filter-option">
                <input type="checkbox" checked={checked} onChange={() => onToggle(issue.id)} />
                <span className="cm-listing-health__issue-filter-option-copy">
                  <span>{t(`${HEALTH_COPY}.issues.${issue.id}`)}</span>
                  <small>{issue.sampleCount ?? 0}</small>
                </span>
              </label>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function listingLocation(listing) {
  const parts = [listing.neighborhood, listing.municipality].filter(Boolean);
  return parts.length ? parts.join(", ") : listing.title;
}

function KpiCard({ label, value, hint, tone = "slate", onClick, active }) {
  const className = `partner-kpi-card partner-kpi-card--${tone}${active ? " is-active" : ""}${
    onClick ? " partner-kpi-card--action" : ""
  }`;
  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        <span className="partner-kpi-card__label">{label}</span>
        <strong className="partner-kpi-card__value">{value ?? "—"}</strong>
        {hint ? <small className="partner-kpi-card__hint">{hint}</small> : null}
      </button>
    );
  }
  return (
    <article className={className}>
      <span className="partner-kpi-card__label">{label}</span>
      <strong className="partner-kpi-card__value">{value ?? "—"}</strong>
      {hint ? <small className="partner-kpi-card__hint">{hint}</small> : null}
    </article>
  );
}

export default function PartnerCasaMapaListingsPage() {
  const { path } = usePartnerPortalBase();
  const { t, locale } = useLocale();
  const { formatMoney } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showInsights, setShowInsights] = useState(false);
  const [contributeOpen, setContributeOpen] = useState(false);
  const [syncSaving, setSyncSaving] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [visibilityBusyId, setVisibilityBusyId] = useState("");

  const agencySlug = searchParams.get("agencySlug") || "";
  const listingType = searchParams.get("listingType") || "";
  const selectedIssues = useMemo(() => parseIssueFilters(searchParams), [searchParams]);
  const search = searchParams.get("q") || "";
  const queuePage = Math.max(1, Number(searchParams.get("page")) || 1);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await fetchCasaMapaPartnerListingHealth({
        agencySlug: agencySlug || undefined,
        listingType: listingType || undefined,
        issue: selectedIssues.length ? selectedIssues.join(",") : undefined,
        q: search.trim() || undefined,
        page: queuePage,
      });
      setDashboard(result.dashboard || result || null);
    } catch (err) {
      setError(formatListingsLoadError(err, t));
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [agencySlug, listingType, queuePage, search, selectedIssues, t]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const updateFilter = (key, value, { resetPage = key !== "page" } = {}) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (resetPage) next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const updateIssueFilters = (nextIssues) => {
    const next = new URLSearchParams(searchParams);
    next.delete("issue");
    if (nextIssues.length) next.set("issues", nextIssues.join(","));
    else next.delete("issues");
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const toggleIssue = (issueId) => {
    const nextIssues = selectedIssues.includes(issueId)
      ? selectedIssues.filter((id) => id !== issueId)
      : [...selectedIssues, issueId];
    updateIssueFilters(nextIssues);
  };

  const summary = dashboard?.summary;
  const issues = dashboard?.issues || [];
  const listings = dashboard?.listings || [];
  const queue = dashboard?.queue || {
    total: listings.length,
    page: queuePage,
    pageSize: QUEUE_PAGE_SIZE,
    totalPages: 1,
  };
  const agencies = dashboard?.agencies || [];
  const agencyOptions = dashboard?.agencyOptions || [];
  const listingSync = dashboard?.listingSync || null;
  const syncMode = listingSync?.mode || "live";

  const saveSyncMode = async (mode, minConfidence = listingSync?.prefs?.minConfidence) => {
    try {
      setSyncSaving(true);
      setSyncError("");
      await updateCasaMapaPartnerListingSync({
        agencySlug: agencySlug || undefined,
        mode,
        minConfidence: mode === "review" ? minConfidence : listingSync?.prefs?.minConfidence,
      });
      await loadDashboard();
    } catch (err) {
      setSyncError(err?.message || t(`${COPY}.sync.saveError`));
    } finally {
      setSyncSaving(false);
    }
  };

  const setListingVisibility = async (listing, publicVisible) => {
    try {
      setVisibilityBusyId(listing.id);
      setSyncError("");
      await updateCasaMapaPartnerListingVisibility(listing.id, {
        publicVisible,
        confidenceScore: listing.confidenceScore ?? listing.healthScore,
      });
      await loadDashboard();
    } catch (err) {
      setSyncError(err?.message || t(`${COPY}.sync.visibilityError`));
    } finally {
      setVisibilityBusyId("");
    }
  };
  const scopeNames = dashboard?.scopeLabel?.length
    ? dashboard.scopeLabel
    : agencyOptions.map((agency) => agency.name).filter(Boolean);

  const topIssue = useMemo(
    () =>
      [...issues]
        .sort((a, b) => (b.sampleCount ?? 0) - (a.sampleCount ?? 0))
        .find((item) => item.sampleCount > 0),
    [issues]
  );

  const visibleIssues = useMemo(
    () => issues.filter((item) => (item.sampleCount ?? 0) > 0).slice(0, 4),
    [issues]
  );
  const hasActiveFilters = Boolean(selectedIssues.length || agencySlug || listingType || search);
  const noLinkedAgencies = !loading && agencyOptions.length === 0 && !summary?.totalListings;
  const hasInventory = Boolean(summary?.totalListings);
  const needsAttention = (summary?.listingsWithIssues ?? 0) > 0;

  const nextAction = useMemo(() => {
    if (noLinkedAgencies) {
      return {
        title: t(`${COPY}.nextAction.linkAgencyTitle`),
        body: t(`${COPY}.nextAction.linkAgencyBody`),
        cta: t(`${COPY}.completeProfile`),
        to: path("profile"),
        tone: "neutral",
      };
    }
    if (topIssue) {
      return {
        title: t(`${COPY}.nextAction.fixIssueTitle`),
        body: t(`${COPY}.priorityBody`, {
          issue: t(`${HEALTH_COPY}.issues.${topIssue.id}`),
          count: topIssue.sampleCount,
        }),
        cta: t(`${COPY}.reviewIssue`),
        issueId: topIssue.id,
        tone: "urgent",
      };
    }
    if (hasInventory) {
      return {
        title: t(`${COPY}.nextAction.healthyTitle`),
        body: t(`${COPY}.nextAction.healthyBody`),
        cta: t(`${COPY}.nextAction.reviewLeads`),
        to: path("leads"),
        tone: "ok",
      };
    }
    return {
      title: t(`${COPY}.emptyTitle`),
      body: t(`${COPY}.emptyBody`),
      cta: t(`${COPY}.completeProfile`),
      to: path("profile/inventory"),
      tone: "neutral",
    };
  }, [hasInventory, noLinkedAgencies, path, t, topIssue]);

  return (
    <div className="cm-partner-listings">
      <header className="cm-partner-listings__hero partner-dashboard-hero">
        <div>
          <p className="cm-partner-listings__eyebrow">{t(`${COPY}.eyebrow`)}</p>
          <h1>{t(`${COPY}.title`)}</h1>
          <p className="partner-dashboard-hero__intro">{t(`${COPY}.subtitle`)}</p>
          {scopeNames.length ? (
            <p className="cm-partner-listings__scope" role="status">
              {t(`${COPY}.scopeNote`, {
                agencies:
                  scopeNames.length <= 2
                    ? scopeNames.join(", ")
                    : t(`${COPY}.scopeNoteMany`, {
                        first: scopeNames[0],
                        count: scopeNames.length - 1,
                      }),
              })}
            </p>
          ) : null}
        </div>
        <div className="partner-dashboard-hero__actions">
          <button
            type="button"
            className="partner-dashboard-btn partner-dashboard-btn--primary"
            onClick={() => setContributeOpen(true)}
          >
            {t(`${COPY}.contributeData`)}
          </button>
          <button type="button" className="partner-dashboard-btn" onClick={loadDashboard}>
            {t(`${COPY}.refresh`)}
          </button>
          <Link className="partner-dashboard-btn" to={path("leads")}>
            {t(`${COPY}.goToLeads`)}
          </Link>
        </div>
      </header>

      <PartnerListingDataContributionWizard
        open={contributeOpen}
        onClose={() => setContributeOpen(false)}
        agencies={agencyOptions}
        defaultAgencySlug={agencySlug}
      />

      {error ? <p className="admin-data-health-error">{error}</p> : null}
      {syncError ? <p className="admin-data-health-error">{syncError}</p> : null}
      {loading ? <p className="muted">{t(`${COPY}.loading`)}</p> : null}

      {!loading ? (
        <section className="partner-panel cm-partner-listings__sync" aria-label={t(`${COPY}.sync.title`)}>
          <div>
            <h2>{t(`${COPY}.sync.title`)}</h2>
            <p className="muted">{t(`${COPY}.sync.subtitle`)}</p>
          </div>
          <div className="cm-partner-listings__sync-modes" role="radiogroup" aria-label={t(`${COPY}.sync.title`)}>
            {[
              { id: "off", label: t(`${COPY}.sync.off`), hint: t(`${COPY}.sync.offHint`) },
              { id: "live", label: t(`${COPY}.sync.live`), hint: t(`${COPY}.sync.liveHint`) },
              { id: "review", label: t(`${COPY}.sync.review`), hint: t(`${COPY}.sync.reviewHint`) },
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                className={`cm-partner-listings__sync-card${syncMode === option.id ? " is-active" : ""}`}
                aria-pressed={syncMode === option.id}
                disabled={syncSaving}
                onClick={() => saveSyncMode(option.id)}
              >
                <strong>{option.label}</strong>
                <span>{option.hint}</span>
              </button>
            ))}
          </div>
          {syncMode === "review" ? (
            <label className="cm-partner-listings__sync-confidence">
              <span>{t(`${COPY}.sync.minConfidence`)}</span>
              <input
                type="number"
                min="0"
                max="100"
                placeholder={t(`${COPY}.sync.minConfidenceNone`)}
                defaultValue={listingSync?.prefs?.minConfidence ?? ""}
                disabled={syncSaving}
                onBlur={(event) => {
                  const value = event.target.value === "" ? null : Number(event.target.value);
                  saveSyncMode("review", value);
                }}
              />
              <small>{t(`${COPY}.sync.minConfidenceHint`)}</small>
            </label>
          ) : null}
        </section>
      ) : null}

      {!loading && nextAction ? (
        <section
          className={`cm-partner-listings__next partner-panel cm-partner-listings__next--${nextAction.tone}`}
        >
          <div>
            <p className="cm-partner-listings__next-kicker">{t(`${COPY}.nextAction.kicker`)}</p>
            <h2>{nextAction.title}</h2>
            <p>{nextAction.body}</p>
          </div>
          {nextAction.to ? (
            <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={nextAction.to}>
              {nextAction.cta}
            </Link>
          ) : (
            <button
              type="button"
              className="partner-dashboard-btn partner-dashboard-btn--primary"
              onClick={() => nextAction.issueId && toggleIssue(nextAction.issueId)}
            >
              {nextAction.cta}
            </button>
          )}
        </section>
      ) : null}

      {!loading && hasInventory ? (
        <>
          <section className="partner-kpi-grid" aria-label={t(`${COPY}.kpiLabel`)}>
            <KpiCard
              label={t(`${COPY}.kpi.total`)}
              value={summary.totalListings?.toLocaleString(locale)}
              hint={t(`${COPY}.kpi.saleRentHint`)}
              tone="blue"
            />
            <KpiCard
              label={t(`${COPY}.kpi.withIssues`)}
              value={summary.listingsWithIssues?.toLocaleString(locale)}
              hint={t(`${COPY}.kpi.healthyHint`, { percent: summary.healthyPercent ?? 0 })}
              tone="amber"
              active={needsAttention && !selectedIssues.length}
              onClick={
                topIssue
                  ? () => {
                      if (!selectedIssues.includes(topIssue.id)) toggleIssue(topIssue.id);
                    }
                  : undefined
              }
            />
            <KpiCard
              label={t(`${COPY}.kpi.avgHealthScore`)}
              value={summary.averageHealthScore != null ? summary.averageHealthScore : "—"}
              hint={t(`${COPY}.kpi.avgHealthHint`)}
              tone="green"
            />
            <KpiCard
              label={t(`${COPY}.kpi.saleRent`)}
              value={`${(summary.saleListings ?? 0).toLocaleString(locale)} / ${(summary.rentListings ?? 0).toLocaleString(locale)}`}
              hint={t(`${COPY}.kpi.yoursOnly`)}
              tone="slate"
            />
            <KpiCard
              label={t(`${COPY}.kpi.hiddenPending`)}
              value={(summary.hiddenPending ?? 0).toLocaleString(locale)}
              hint={t(`${COPY}.kpi.hiddenPendingHint`)}
              tone={summary.hiddenPending ? "amber" : "green"}
            />
            <KpiCard
              label={t(`${COPY}.kpi.avgConfidence`)}
              value={summary.averageConfidenceScore != null ? summary.averageConfidenceScore : "—"}
              hint={t(`${COPY}.kpi.avgConfidenceHint`)}
              tone="blue"
            />
          </section>

          {visibleIssues.length ? (
            <section className="partner-panel cm-partner-listings__actions-panel">
              <div className="cm-listing-health__panel-head">
                <h2>{t(`${COPY}.sections.issues`)}</h2>
                <p className="muted">{t(`${COPY}.sections.issuesHint`)}</p>
              </div>
              <div className="cm-partner-listings__action-grid">
                {visibleIssues.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`cm-partner-listings__action-card${
                      selectedIssues.includes(item.id) ? " is-active" : ""
                    }`}
                    onClick={() => toggleIssue(item.id)}
                  >
                    <span className={`cm-listing-health__severity cm-listing-health__severity--${item.severity}`}>
                      {t(`${HEALTH_COPY}.severity.${item.severity}`)}
                    </span>
                    <strong>{item.sampleCount}</strong>
                    <h3>{t(`${HEALTH_COPY}.issues.${item.id}`)}</h3>
                    <p>{t(`${COPY}.fixNow`)}</p>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          <div className="cm-listing-health__filters">
            <div className="cm-listing-health__filters-bar">
              <label className="cm-listing-health__search">
                <span className="cm-listing-health__search-icon" aria-hidden="true">
                  ⌕
                </span>
                <input
                  type="search"
                  className="cm-listing-health__search-input"
                  placeholder={t(`${COPY}.searchPlaceholder`)}
                  value={search}
                  onChange={(event) => updateFilter("q", event.target.value)}
                />
              </label>

              <div className="cm-listing-health__filter-group">
                <label className="cm-listing-health__filter-field">
                  <span className="cm-listing-health__filter-label">{t(`${HEALTH_COPY}.typeFilterLabel`)}</span>
                  <select
                    className="cm-listing-health__select"
                    value={listingType}
                    onChange={(event) => updateFilter("listingType", event.target.value)}
                  >
                    <option value="">{t("realEstate.casaMapa.admin.listingType.all")}</option>
                    <option value="sale">{t("realEstate.casaMapa.admin.listingType.sale")}</option>
                    <option value="rent">{t("realEstate.casaMapa.admin.listingType.rent")}</option>
                  </select>
                </label>

                {agencyOptions.length > 1 ? (
                  <label className="cm-listing-health__filter-field">
                    <span className="cm-listing-health__filter-label">
                      {t(`${HEALTH_COPY}.agencyFilterLabel`)}
                    </span>
                    <select
                      className="cm-listing-health__select"
                      value={agencySlug}
                      onChange={(event) => updateFilter("agencySlug", event.target.value)}
                    >
                      <option value="">{t(`${COPY}.allYourAgencies`)}</option>
                      {agencyOptions.map((agency) => (
                        <option key={agency.slug} value={agency.slug}>
                          {agency.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>
            </div>

            {hasActiveFilters ? (
              <div className="cm-listing-health__active-filters">
                {search ? (
                  <button
                    type="button"
                    className="cm-listing-health__filter-chip"
                    onClick={() => updateFilter("q", "")}
                  >
                    {t(`${HEALTH_COPY}.activeFilters.search`, { value: search })}
                    <span aria-hidden="true">×</span>
                  </button>
                ) : null}
                {listingType ? (
                  <button
                    type="button"
                    className="cm-listing-health__filter-chip"
                    onClick={() => updateFilter("listingType", "")}
                  >
                    {t(`${HEALTH_COPY}.activeFilters.type`, {
                      value: t(`realEstate.casaMapa.admin.listingType.${listingType}`),
                    })}
                    <span aria-hidden="true">×</span>
                  </button>
                ) : null}
                {agencySlug ? (
                  <button
                    type="button"
                    className="cm-listing-health__filter-chip"
                    onClick={() => updateFilter("agencySlug", "")}
                  >
                    {t(`${HEALTH_COPY}.activeFilters.agency`, {
                      value: agencyOptions.find((agency) => agency.slug === agencySlug)?.name || agencySlug,
                    })}
                    <span aria-hidden="true">×</span>
                  </button>
                ) : null}
                {selectedIssues.map((issueId) => (
                  <button
                    key={issueId}
                    type="button"
                    className="cm-listing-health__filter-chip"
                    onClick={() => toggleIssue(issueId)}
                  >
                    {t(`${HEALTH_COPY}.activeFilters.issue`, {
                      value: t(`${HEALTH_COPY}.issues.${issueId}`),
                    })}
                    <span aria-hidden="true">×</span>
                  </button>
                ))}
                <button
                  type="button"
                  className="cm-listing-health__clear-all"
                  onClick={() => setSearchParams({}, { replace: true })}
                >
                  {t(`${HEALTH_COPY}.clearFilter`)}
                </button>
              </div>
            ) : null}
          </div>

          <section className="partner-panel cm-listing-health__panel" id="listings-queue">
            <div className="cm-listing-health__panel-head cm-listing-health__panel-head--queue">
              <div className="cm-listing-health__queue-head">
                <h2>{t(`${COPY}.sections.queue`)}</h2>
                <span className="cm-listing-health__queue-count">
                  {t(`${HEALTH_COPY}.queueCount`, {
                    count: (queue.total ?? 0).toLocaleString(locale),
                  })}
                </span>
              </div>
              <IssueTypeFilterMenu
                issueOptions={issues}
                selectedIssues={selectedIssues}
                onToggle={toggleIssue}
                onClear={() => updateIssueFilters([])}
                t={t}
              />
            </div>

            {listings.length ? (
              <div className="cm-partner-listings__queue">
                {listings.map((listing, index) => (
                  <article key={listing.id} className="cm-partner-listings__queue-card">
                    {index === 0 && needsAttention ? (
                      <p className="cm-partner-listings__queue-start">{t(`${COPY}.startHere`)}</p>
                    ) : null}
                    <Link className="cm-partner-listings__queue-link" to={path(`listings/${listing.id}`)}>
                      <div className="cm-partner-listings__queue-main">
                        {listing.photoUrl ? (
                          <img src={listing.photoUrl} alt="" className="cm-partner-listings__queue-photo" />
                        ) : (
                          <span
                            className="cm-partner-listings__queue-photo cm-partner-listings__queue-photo--empty"
                            aria-hidden="true"
                          >
                            ⌂
                          </span>
                        )}
                        <div>
                          <h3>{listing.title}</h3>
                          <p className="muted">{listingLocation(listing)}</p>
                          <p>
                            {listing.priceCop != null ? formatMoney(listing.priceCop) : "—"}
                            {listing.listingType
                              ? ` · ${t(
                                  `realEstate.casaMapa.admin.listingType.${listing.listingType}`,
                                  {},
                                  listing.listingType
                                )}`
                              : ""}
                          </p>
                          {listing.agencyName ? (
                            <p className="cm-partner-listings__agency">{listing.agencyName}</p>
                          ) : null}
                        </div>
                      </div>
                      <div className="cm-partner-listings__queue-issues">
                        {(listing.issueIds || []).map((issueId) => (
                          <span key={issueId} className="cm-listing-health__issue-tag">
                            {t(`${HEALTH_COPY}.issues.${issueId}`)}
                          </span>
                        ))}
                      </div>
                      <div className="cm-partner-listings__queue-meta">
                        <span
                          className={`cm-listing-health__score cm-listing-health__score--${
                            listing.healthLabel || "fair"
                          }`}
                        >
                          {t(`${COPY}.meta.confidence`)}:{" "}
                          {listing.confidenceScore != null || listing.healthScore != null
                            ? `${listing.confidenceScore ?? listing.healthScore}`
                            : "—"}
                        </span>
                        <span
                          className={`cm-partner-listings__visibility ${
                            listing.publicVisible === false
                              ? "cm-partner-listings__visibility--hidden"
                              : "cm-partner-listings__visibility--public"
                          }`}
                        >
                          {listing.publicVisible === false
                            ? t(`${COPY}.sync.hiddenBadge`)
                            : t(`${COPY}.sync.publicBadge`)}
                        </span>
                        <span>
                          {t(`${COPY}.meta.photos`)}: {listing.imageCount ?? 0}
                        </span>
                      </div>
                    </Link>
                    <div className="cm-partner-listings__queue-actions">
                      <Link
                        className="partner-dashboard-btn partner-dashboard-btn--primary"
                        to={path(`listings/${listing.id}`)}
                      >
                        {t(`${COPY}.reviewListing`)}
                      </Link>
                      <Link className="partner-dashboard-btn" to={path(`listings/${listing.id}/comps`)}>
                        {t(`${COPY}.openComps`)}
                      </Link>
                      {listing.publicVisible === false ? (
                        <button
                          type="button"
                          className="partner-dashboard-btn partner-dashboard-btn--primary"
                          disabled={visibilityBusyId === listing.id}
                          onClick={() => setListingVisibility(listing, true)}
                        >
                          {t(`${COPY}.sync.publish`)}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="partner-dashboard-btn"
                          disabled={visibilityBusyId === listing.id}
                          onClick={() => setListingVisibility(listing, false)}
                        >
                          {t(`${COPY}.sync.hide`)}
                        </button>
                      )}
                      {listing.publicUrl && listing.publicVisible !== false ? (
                        <a
                          className="partner-dashboard-btn"
                          href={listing.publicUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t(`${COPY}.viewPublic`)}
                        </a>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className="muted">{t(`${COPY}.queueEmpty`)}</p>
            )}

            {queue.totalPages > 1 ? (
              <div className="cm-listing-health__pagination">
                <button
                  type="button"
                  className="partner-dashboard-btn"
                  disabled={queue.page <= 1}
                  onClick={() => updateFilter("page", String(queue.page - 1), { resetPage: false })}
                >
                  {t(`${HEALTH_COPY}.pagination.previous`)}
                </button>
                <span className="muted">
                  {t(`${HEALTH_COPY}.pagination.page`, {
                    page: queue.page,
                    totalPages: queue.totalPages,
                  })}
                </span>
                <button
                  type="button"
                  className="partner-dashboard-btn"
                  disabled={queue.page >= queue.totalPages}
                  onClick={() => updateFilter("page", String(queue.page + 1), { resetPage: false })}
                >
                  {t(`${HEALTH_COPY}.pagination.next`)}
                </button>
              </div>
            ) : null}
          </section>

          <section className="partner-panel cm-partner-listings__insights">
            <button
              type="button"
              className="cm-partner-listings__insights-toggle"
              onClick={() => setShowInsights((value) => !value)}
              aria-expanded={showInsights}
            >
              <span>{t(`${COPY}.sections.insights`)}</span>
              <span aria-hidden="true">{showInsights ? "▲" : "▼"}</span>
            </button>
            {showInsights ? (
              <div className="cm-partner-listings__insights-body">
                <p className="muted">{t(`${COPY}.sections.insightsHint`)}</p>
                {agencyOptions.length > 1 && agencies.length > 0 ? (
                  <div className="cm-listing-health__agency-grid">
                    {agencies.slice(0, 6).map((agency) => (
                      <button
                        key={agency.slug || agency.name}
                        type="button"
                        className={`cm-listing-health__agency-card${
                          agencySlug === agency.slug ? " is-active" : ""
                        }`}
                        onClick={() => updateFilter("agencySlug", agency.slug || "")}
                      >
                        <strong>{agency.name}</strong>
                        <span>
                          {t(`${HEALTH_COPY}.agencyIssues`, { count: agency.listingsWithIssues })}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
                <Link className="partner-dashboard-btn" to={path("listing-corrections")}>
                  {t(`${COPY}.reportCorrection`)}
                </Link>
              </div>
            ) : null}
          </section>
        </>
      ) : null}

      {!loading && !hasInventory && !noLinkedAgencies ? (
        <section className="partner-panel cm-partner-listings__empty">
          <h2>{t(`${COPY}.emptyTitle`)}</h2>
          <p className="muted">{t(`${COPY}.emptyBody`)}</p>
          <p className="muted">{t(`${COPY}.contributeDataHint`)}</p>
          <div className="cm-partner-listings__empty-actions">
            <button
              type="button"
              className="partner-dashboard-btn partner-dashboard-btn--primary"
              onClick={() => setContributeOpen(true)}
            >
              {t(`${COPY}.contributeData`)}
            </button>
            <Link className="partner-dashboard-btn" to={path("profile/inventory")}>
              {t(`${COPY}.completeProfile`)}
            </Link>
            <Link className="partner-dashboard-btn" to={path("market")}>
              {t(`${COPY}.exploreMarket`)}
            </Link>
          </div>
        </section>
      ) : null}
    </div>
  );
}
